// src/components/Scenario/Scenario.js
import React, { useState, useRef, useEffect } from "react";
import Lane from "../Lane/Lane";
import Relation from "../Relation/Relation";
import RelationModal from "../RelationModal/RelationModal";
import ScenarioHeader from "../ScenarioHeader/ScenarioHeader";
import apiService from "../../api";
import ConfirmModal from "../Common/ConfirmModal";
import { useConfirm } from "../../hooks/useConfirm";
import "./Scenario.css";

const Scenario = ({ scenario, updateScenario, addLane, dansMaPoche, isCalculating }) => {
  const [currentLine, setCurrentLine] = useState(null);
  const [editingRelation, setEditingRelation] = useState(null);
  const [pendingRelation, setPendingRelation] = useState(null);
  const [highlightedRelation, setHighlightedRelation] = useState(null);

  // Confirm modal state
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  // Handler to set the primary individual
  const handleSetPrimaryIndividual = (individualId) => {
    if (!scenario || !updateScenario) return;
    updateScenario(scenario.id, (current) => ({
      ...current,
      primaryIndividualId: individualId,
      primary_individual_id: individualId,
      _hasUnsavedChanges: true
    }));
  };

  // Auto-set first individual as primary if none set
  // CRITICAL: Include scenario.id in deps to re-run when switching scenarios
  // This prevents stale closure issues where wrong scenario gets updated
  useEffect(() => {
    if (!scenario || !updateScenario) return;

    const currentScenarioId = scenario.id;
    const primaryId = scenario.primaryIndividualId || scenario.primary_individual_id;

    // If no primary set, find first individual with a valid ID
    if (!primaryId && scenario.boxes?.length > 0) {
      const firstIndividualBox = scenario.boxes.find(
        b => b.content === 'Individu' && (b.individual_id || b.id)
      );
      if (firstIndividualBox) {
        const individualId = firstIndividualBox.individual_id || firstIndividualBox.id;
        if (individualId && !String(individualId).startsWith('temp-')) {
          // Use currentScenarioId from this effect's closure, not from handleSetPrimaryIndividual
          updateScenario(currentScenarioId, (current) => ({
            ...current,
            primaryIndividualId: individualId,
            primary_individual_id: individualId,
            _hasUnsavedChanges: true
          }));
        }
      }
    }
  }, [scenario?.id, scenario?.boxes?.length, scenario?.primaryIndividualId, updateScenario]);

  const scenarioRef = useRef(null);
  const svgRef = useRef(null);

  // Listen for highlightRelation events from EntityEditPage
  useEffect(() => {
    const handleHighlightRelation = (event) => {
      const { individualId, entityId } = event.detail;
      // Find the relation that connects this individual to this entity
      if (scenario?.relations) {
        const relation = scenario.relations.find(r => {
          const startBox = scenario.boxes.find(b => b.uniqueBoxId === r.startBox?.boxId);
          const endBox = scenario.boxes.find(b => b.uniqueBoxId === r.endBox?.boxId);
          return (
            (startBox?.individual_id === individualId && endBox?.entity_id === entityId) ||
            (endBox?.individual_id === individualId && startBox?.entity_id === entityId)
          );
        });
        if (relation) {
          setHighlightedRelation(relation.id);
          // Clear highlight after 3 seconds
          setTimeout(() => setHighlightedRelation(null), 3000);
        }
      }
    };

    window.addEventListener('highlightRelation', handleHighlightRelation);
    return () => window.removeEventListener('highlightRelation', handleHighlightRelation);
  }, [scenario]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (svgRef.current && scenarioRef.current) {
        svgRef.current.setAttribute("width", scenarioRef.current.offsetWidth);
        svgRef.current.setAttribute("height", scenarioRef.current.offsetHeight);
      }
    });

    if (scenarioRef.current) {
      resizeObserver.observe(scenarioRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    console.log("[Scenario] Received scenario:", scenario);
    if (!scenario) return;
    console.log("[Scenario] Relations:", scenario.relations);

    if (scenario.relations) {
      console.log('[Scenario] Setting up relations for scenario:', scenario.id);
      console.log('[Scenario] Initial relations data:', scenario.relations);

      const updatedRelations = scenario.relations.map(relation => {
        const startBox = scenario.boxes.find(box => 
          box.uniqueBoxId === relation.startBox.boxId
        );
        const endBox = scenario.boxes.find(box => 
          box.uniqueBoxId === relation.endBox.boxId
        );

        console.log('[Scenario] Processing relation:', relation);
        console.log('[Scenario] Start box:', startBox);
        console.log('[Scenario] End box:', endBox);

        if (!startBox || !endBox) {
          console.warn(`[Scenario] Could not find boxes for relation: ${relation.id}`);
          return null;
        }

        const updatedRelation = {
          ...relation,
          startBox: { ...relation.startBox, laneId: startBox.laneId },
          endBox: { ...relation.endBox, laneId: endBox.laneId }
        };
        console.log('[Scenario] Updated relation:', updatedRelation);
        return updatedRelation;
      }).filter(Boolean);

      console.log('[Scenario] Updated relations:', updatedRelations);

      if (JSON.stringify(updatedRelations) !== JSON.stringify(scenario.relations)) {
        console.log('[Scenario] Updating scenario with new relations');
        updateScenario(scenario.id, current => ({
          ...current,
          relations: updatedRelations
        }));
      }
    }
  }, [scenario, updateScenario]);

  if (!scenario) {
    console.warn("[Scenario] No scenario provided to Scenario component");
    return <div>No scenario selected</div>;
  }

  const { lanes = [], boxes = [], relations = [], selectedBoxes = [] } = scenario;

  console.log('[Scenario] Rendering Scenario component with:', { lanes, boxes, relations, selectedBoxes });

  const startLineDrawing = (startBox, startCoords) => {
    const scenarioRect = scenarioRef.current.getBoundingClientRect();
    const boxElement = document.querySelector(
      `[data-lane-id="${startBox.laneId}"][data-box-id="${startBox.boxId}"]`
    );
    if (boxElement) {
      const boxRect = boxElement.getBoundingClientRect();
      setCurrentLine({
        startBox,
        startX: boxRect.left + boxRect.width / 2 - scenarioRect.left,
        startY: boxRect.bottom - scenarioRect.top,
        endX: startCoords.x - scenarioRect.left,
        endY: startCoords.y - scenarioRect.top,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (currentLine) {
      const scenarioRect = scenarioRef.current.getBoundingClientRect();
      setCurrentLine({
        ...currentLine,
        endX: e.clientX - scenarioRect.left,
        endY: e.clientY - scenarioRect.top,
      });
    }
  };

  const endLineDrawing = (endBox, endCoords) => {
    if (currentLine && endBox.laneId !== currentLine.startBox.laneId) {
      const newRelation = {
        id: `${currentLine.startBox.boxId}-${endBox.boxId}`,
        startBox: currentLine.startBox,
        endBox: endBox,
        percentage: 0,
      };
      setPendingRelation(newRelation);
      setEditingRelation(newRelation);
    }
    setCurrentLine(null);
  };

  const deleteRelation = (id) => {
    // Update local state INSTANTLY - persistence happens when user clicks "Enregistrer"
    const relation = scenario.relations?.find(rel => rel.id === id);
    if (!relation) return;

    updateScenario(scenario.id, (current) => {
      // Find the item IDs
      const entityBoxId = relation.endBox?.boxId;
      const individualBoxId = relation.startBox?.boxId;
      const role = relation.role?.toLowerCase();
      const dbId = relation.dbId;

      const itemsArray = current.items || current.boxes || [];

      // Update entities array - clear manager's individualId
      const updatedEntities = (current.entities || []).map(ent => {
        const entItem = itemsArray.find(b => b.uniqueBoxId === entityBoxId && (b.type === 'entity' || b.content === 'Entity'));
        if (entItem && (ent.id === entItem.id || ent.id === entItem.entity_id)) {
          const managers = typeof ent.managers === 'string' ? JSON.parse(ent.managers) : (ent.managers || []);
          const updatedManagers = managers.map(m => {
            if (m.role?.toLowerCase() === role) {
              return { ...m, individualId: null };
            }
            return m;
          });
          return { ...ent, managers: updatedManagers };
        }
        return ent;
      });

      // Update items array (single source of truth)
      const updatedItems = itemsArray.map(item => {
        // Update entity item - clear manager link and track cleared role
        if (item.uniqueBoxId === entityBoxId && (item.type === 'entity' || item.content === 'Entity')) {
          const managers = typeof item.managers === 'string' ? JSON.parse(item.managers) : (item.managers || []);
          const updatedManagers = managers.map(m => {
            if (m.role?.toLowerCase() === role) {
              return { ...m, individualId: null };
            }
            return m;
          });
          // Track cleared manager roles for EntityEditPage to filter
          const clearedManagerRoles = [...(item._clearedManagerRoles || []), role].filter(Boolean);
          return { ...item, managers: updatedManagers, _clearedManagerRoles: clearedManagerRoles };
        }
        // Update individual item - track deleted shareholding to hide related income
        if (item.uniqueBoxId === individualBoxId && (item.type === 'individual' || item.content === 'Individu')) {
          const deletedShareholdingIds = [...(item._deletedShareholdingIds || []), dbId].filter(Boolean);
          return { ...item, _deletedShareholdingIds: deletedShareholdingIds };
        }
        return item;
      });

      // Also remove from shareholdings array
      const shareholdingToRemove = relation.dbId;
      const updatedShareholdings = (current.shareholdings || []).filter(sh =>
        sh.id !== shareholdingToRemove && sh.dbId !== shareholdingToRemove
      );

      return {
        ...current,
        relations: current.relations.filter((rel) => rel.id !== id),
        entities: updatedEntities,
        items: updatedItems,        // Single source of truth
        boxes: updatedItems,        // Alias for backward compatibility
        shareholdings: updatedShareholdings,
        // Track deleted relations for cleanup on save
        _deletedRelations: [...(current._deletedRelations || []), relation].filter(Boolean),
        _hasUnsavedChanges: true
      };
    });
  };

  const updateRelation = async (relationData) => {
    const { id } = editingRelation;

    // Get the boxes for this relation to find their DB IDs
    const startBox = boxes.find(b => b.uniqueBoxId === editingRelation.startBox.boxId);
    const endBox = boxes.find(b => b.uniqueBoxId === editingRelation.endBox.boxId);

    if (pendingRelation && pendingRelation.id === id) {
      // Creating a new relation
      let dbId = null;

      // DEBUG: Log box lookup
      console.log('[Scenario] updateRelation - CREATING NEW RELATION for scenario:', scenario.id);
      console.log('[Scenario] updateRelation - editingRelation:', editingRelation);
      console.log('[Scenario] updateRelation - boxes array:', boxes.map(b => ({ uniqueBoxId: b.uniqueBoxId, id: b.id, individual_id: b.individual_id, entity_id: b.entity_id, content: b.content })));
      console.log('[Scenario] updateRelation - startBox found:', startBox);
      console.log('[Scenario] updateRelation - endBox found:', endBox);

      // ISSUE #49 FIX: Validate IDs belong to current scenario before creating shareholding
      const isIndividualStart = startBox?.type === 'individual' || startBox?.content === 'Individu';
      const individualId = isIndividualStart ? (startBox.id || startBox.individual_id) : null;
      const entityOwnerId = !isIndividualStart ? (startBox.id || startBox.entity_id) : null;
      const entityOwnedId = endBox?.id || endBox?.entity_id;

      // Use items[] as source of truth for validation (with fallback to individuals/entities)
      const itemsArray = scenario.items || scenario.boxes || [];

      // Validate individual belongs to this scenario
      if (individualId) {
        const validIndividual = itemsArray.some(item =>
          (item.type === 'individual' || item.content === 'Individu') &&
          (item.id === individualId || item.individual_id === individualId)
        ) || (scenario.individuals || []).some(ind =>
          (ind.id === individualId || ind.individual_id === individualId)
        );
        if (!validIndividual) {
          console.error(`[Scenario] CRITICAL: Individual ${individualId} does not belong to scenario ${scenario.id}!`);
          console.error('[Scenario] Available items:', itemsArray.map(i => ({ type: i.type, id: i.id })));
          showConfirm({
            title: 'Erreur de données',
            message: "L'individu sélectionné n'appartient pas à ce scénario. Veuillez rafraîchir la page et réessayer.",
            confirmText: 'OK',
            cancelText: '',
            type: 'danger',
            onConfirm: () => {}
          });
          setEditingRelation(null);
          setPendingRelation(null);
          return;
        }
      }

      // Validate entity belongs to this scenario
      if (entityOwnedId) {
        const validEntity = itemsArray.some(item =>
          (item.type === 'entity' || item.content === 'Entity') &&
          (item.id === entityOwnedId || item.entity_id === entityOwnedId)
        ) || (scenario.entities || []).some(ent =>
          (ent.id === entityOwnedId || ent.entity_id === entityOwnedId)
        );
        if (!validEntity) {
          console.error(`[Scenario] CRITICAL: Entity ${entityOwnedId} does not belong to scenario ${scenario.id}!`);
          console.error('[Scenario] Available items:', itemsArray.map(i => ({ type: i.type, id: i.id })));
          showConfirm({
            title: 'Erreur de données',
            message: "L'entité sélectionnée n'appartient pas à ce scénario. Veuillez rafraîchir la page et réessayer.",
            confirmText: 'OK',
            cancelText: '',
            type: 'danger',
            onConfirm: () => {}
          });
          setEditingRelation(null);
          setPendingRelation(null);
          return;
        }
      }

      // Try to save to API (may fail for unsaved scenarios)
      console.log('[Scenario] updateRelation - Validated IDs, calling createShareholding API');
      try {
        const shareholdingData = {
          scenario_id: scenario.id,
          individual_owner_id: individualId,
          entity_owner_id: entityOwnerId,
          entity_owned_id: entityOwnedId,
          percentage: relationData.percentage,
          role: relationData.role,
          is_manager: relationData.isManager,
          receive_dividends: relationData.receiveDividends,
          entry_date: relationData.entryDate,
          dividend_tax_option: relationData.dividendTaxOption || 'PFU',
          acompte_dispense: relationData.acompteDispense || false
        };

        const savedShareholding = await apiService.createShareholding(shareholdingData);
        dbId = savedShareholding.id;

        // NOTE: Manager income is NOT created here at relation time.
        // Income is created when entity is saved via EntityEditPage,
        // which triggers PATCH /api/entities with proper manager config.
        // The income comes FROM the entity, not from template hypotheticals.
      } catch (error) {
        console.error('Error creating shareholding (will save with scenario):', error);
      }

      console.log('[Scenario] updateRelation - API call done, dbId:', dbId);

      // Always add relation to local state for visual rendering
      // AND add to shareholdings array for calculations and saving
      console.log('[Scenario] updateRelation - Adding to relations AND shareholdings arrays');
      const newRole = relationData.role?.toLowerCase();
      updateScenario(scenario.id, (current) => {
        const itemsArray = current.items || current.boxes || [];
        // Clear this role from _clearedManagerRoles on entity item (if it was previously cleared)
        const updatedItems = itemsArray.map(item => {
          if (item.uniqueBoxId === endBox?.uniqueBoxId && (item.type === 'entity' || item.content === 'Entity') && item._clearedManagerRoles) {
            const clearedManagerRoles = item._clearedManagerRoles.filter(r => r !== newRole);
            return { ...item, _clearedManagerRoles: clearedManagerRoles };
          }
          // Also clear _deletedShareholdingIds on individual item when re-linking
          if (item.uniqueBoxId === startBox?.uniqueBoxId && (item.type === 'individual' || item.content === 'Individu') && item._deletedShareholdingIds && dbId) {
            // We can't easily remove the old shareholding ID since it's deleted, but new link has new dbId
            // The filtering will work because the new income has a new shareholding_id
          }
          return item;
        });

        // Create shareholding entry for calculations and saving
        // Must match format expected by scenarioCalculations.js
        // Note: Uses validated individualId, entityOwnerId, entityOwnedId from closure (Issue #49 fix)
        const newShareholding = {
          id: dbId,
          scenario_id: scenario.id,
          individual_owner_id: individualId,
          individual_scenario_owner_id: individualId,
          entity_owner_id: entityOwnerId,
          entity_scenario_owner_id: entityOwnerId,
          entity_owned_id: entityOwnedId,
          entity_scenario_owned_id: entityOwnedId,
          owned_id: entityOwnedId,
          percentage: relationData.percentage,
          role: relationData.role,
          is_manager: relationData.isManager,
          receive_dividends: relationData.receiveDividends,
          entry_date: relationData.entryDate,
          entryDate: relationData.entryDate,
          dividend_tax_option: relationData.dividendTaxOption || 'PFU',
          dividendTaxOption: relationData.dividendTaxOption || 'PFU',
          acompte_dispense: relationData.acompteDispense || false,
          acompteDispense: relationData.acompteDispense || false
        };

        return {
          ...current,
          items: updatedItems,        // Single source of truth
          boxes: updatedItems,        // Alias for backward compatibility
          relations: [
            ...current.relations,
            {
              ...pendingRelation,
              dbId: dbId,
              percentage: relationData.percentage,
              role: relationData.role,
              isManager: relationData.isManager,
              receiveDividends: relationData.receiveDividends,
              entryDate: relationData.entryDate,
              acompteDispense: relationData.acompteDispense || false
            },
          ],
          // Add to shareholdings array for calculations and saving
          shareholdings: [
            ...(current.shareholdings || []),
            newShareholding
          ],
          _hasUnsavedChanges: true
        };
      });
      setPendingRelation(null);
    } else {
      // Updating existing relation
      try {
        if (editingRelation.dbId) {
          await apiService.updateShareholding(editingRelation.dbId, {
            percentage: relationData.percentage,
            role: relationData.role,
            is_manager: relationData.isManager,
            receive_dividends: relationData.receiveDividends,
            entry_date: relationData.entryDate,
            dividend_tax_option: relationData.dividendTaxOption || 'PFU',
            acompte_dispense: relationData.acompteDispense || false
          });

          // Also update entity manager's entryDate if this is a manager role
          if (relationData.isManager && startBox?.content === 'Individu' && endBox?.id) {
            const entityId = endBox.id;
            const individualId = startBox.id;
            try {
              const entity = await apiService.getEntity(entityId);
              const managers = entity.managers || [];
              const managerRole = relationData.role?.toLowerCase();

              const updatedManagers = managers.map(m => {
                if (m.role?.toLowerCase() === managerRole) {
                  return { ...m, individualId, entryDate: relationData.entryDate };
                }
                return m;
              });

              await apiService.updateEntity(entityId, { managers: updatedManagers });
              console.log('[Scenario] Updated entity manager entryDate on relation edit:', entityId, relationData.role, relationData.entryDate);
            } catch (err) {
              console.error('[Scenario] Error updating entity manager entryDate:', err);
            }
          }
        }

        updateScenario(scenario.id, (current) => ({
          ...current,
          relations: current.relations.map((rel) =>
            rel.id === id
              ? {
                  ...rel,
                  percentage: relationData.percentage,
                  role: relationData.role,
                  isManager: relationData.isManager,
                  receiveDividends: relationData.receiveDividends,
                  entryDate: relationData.entryDate,
                  acompteDispense: relationData.acompteDispense || false
                }
              : rel
          ),
          // Also update shareholdings array for calculations
          shareholdings: (current.shareholdings || []).map((sh) =>
            sh.id === editingRelation.dbId || sh.dbId === editingRelation.dbId
              ? {
                  ...sh,
                  percentage: relationData.percentage,
                  role: relationData.role,
                  is_manager: relationData.isManager,
                  receive_dividends: relationData.receiveDividends,
                  entry_date: relationData.entryDate,
                  entryDate: relationData.entryDate,
                  dividend_tax_option: relationData.dividendTaxOption || 'PFU',
                  dividendTaxOption: relationData.dividendTaxOption || 'PFU',
                  acompte_dispense: relationData.acompteDispense || false,
                  acompteDispense: relationData.acompteDispense || false
                }
              : sh
          ),
          _hasUnsavedChanges: true
        }));
      } catch (error) {
        console.error('Error updating shareholding:', error);
      }
    }
    setEditingRelation(null);
  };

  const removeLane = (idToRemove) => {
    updateScenario(scenario.id, (current) => {
      // Find relations being removed to also remove their shareholdings
      const removedRelations = current.relations.filter(
        (relation) =>
          relation.startBox.laneId === idToRemove ||
          relation.endBox.laneId === idToRemove
      );
      const removedDbIds = removedRelations.map(r => r.dbId).filter(Boolean);

      // Filter items (single source of truth)
      const newItems = (current.items || current.boxes || []).filter((item) => item.laneId !== idToRemove);

      // Find items being removed for filtering individuals/entities
      const removedItems = (current.items || current.boxes || []).filter((item) => item.laneId === idToRemove);
      const removedIndividualIds = removedItems
        .filter(i => i.type === 'individual' || i.content === 'Individu')
        .map(i => i.id || i.individual_id);
      const removedEntityIds = removedItems
        .filter(i => i.type === 'entity' || i.content === 'Entity')
        .map(i => i.id || i.entity_id);

      return {
        ...current,
        lanes: current.lanes
          .filter((lane) => lane.id !== idToRemove)
          .map((lane, index) => ({ ...lane, level: index })),
        selectedBoxes: current.selectedBoxes.filter(
          (box) => box.laneId !== idToRemove
        ),
        relations: current.relations.filter(
          (relation) =>
            relation.startBox.laneId !== idToRemove &&
            relation.endBox.laneId !== idToRemove
        ),
        items: newItems,        // Single source of truth
        boxes: newItems,        // Alias for backward compatibility
        // Also filter individuals/entities for backend compatibility
        individuals: (current.individuals || []).filter(ind =>
          !removedIndividualIds.includes(ind.id) && !removedIndividualIds.includes(ind.individual_id)
        ),
        entities: (current.entities || []).filter(ent =>
          !removedEntityIds.includes(ent.id) && !removedEntityIds.includes(ent.entity_id)
        ),
        // Also remove corresponding shareholdings
        shareholdings: (current.shareholdings || []).filter(
          (sh) => !removedDbIds.includes(sh.id) && !removedDbIds.includes(sh.dbId)
        ),
      };
    });
  };

  const onBoxClick = (laneId, boxId) => {
    updateScenario(scenario.id, (current) => {
      const currentSelectedBoxes = current.selectedBoxes || [];
      const isSelected = currentSelectedBoxes.some(
        (box) => box.laneId === laneId && box.boxId === boxId
      );
      const updatedSelectedBoxes = isSelected
        ? currentSelectedBoxes.filter(
            (box) => !(box.laneId === laneId && box.boxId === boxId)
          )
        : [...currentSelectedBoxes, { laneId, boxId }];

      return {
        ...current,
        selectedBoxes: updatedSelectedBoxes,
      };
    });
  };

  const addBox = (laneId, newBox) => {
    updateScenario(scenario.id, (current) => {
      // Determine type from content field
      const type = newBox.content === 'Individu' ? 'individual' : 'entity';
      const dbId = type === 'individual'
        ? (newBox.id || newBox.individual_id)
        : (newBox.id || newBox.entity_id);

      // Create unified item with ALL fields
      const newItem = {
        // Type identifier
        type,
        // Standard ID format
        id: dbId,
        uniqueBoxId: newBox.uniqueBoxId || `${type}-${dbId}`,
        // Rendering fields
        content: newBox.content,
        headerColor: newBox.headerColor,
        laneId,
        boxPosition: (current.items || current.boxes || []).filter(b => b.laneId === laneId).length,
        // Position fields for backend
        lane_position: laneId,
        box_position: (current.items || current.boxes || []).filter(b => b.laneId === laneId).length,
        // ALL other fields from newBox
        ...newBox
      };

      // Update items array (single source of truth)
      const newItems = [...(current.items || current.boxes || []), newItem];

      const updates = {
        ...current,
        items: newItems,        // Single source of truth
        boxes: newItems,        // Alias for backward compatibility
      };

      // Also update individuals/entities arrays for backend compatibility
      if (type === 'individual') {
        const newIndividual = {
          id: dbId,
          individual_id: newBox.individual_id || dbId,
          template_id: newBox.template_id,
          lane_position: laneId,
          box_position: newItem.box_position,
          first_name: newBox.first_name || '',
          last_name: newBox.last_name || '',
          marital_status: newBox.marital_status || 'Célibataire',
          children_count: newBox.children_count || 0,
          single_parent: newBox.single_parent || false,
          employment_type: newBox.employment_type || '',
          fiscal_residence: newBox.fiscal_residence || 'France',
          _tempId: newItem.uniqueBoxId
        };
        updates.individuals = [...(current.individuals || []), newIndividual];
      } else {
        const newEntity = {
          id: dbId,
          entity_id: newBox.entity_id || dbId,
          template_id: newBox.template_id,
          lane_position: laneId,
          box_position: newItem.box_position,
          entity_name: newBox.entity_name || '',
          entity_type: newBox.entity_type || '',
          tax_regime: newBox.tax_regime || 'IS',
          activity_type: newBox.activity_type || '',
          fiscal_year_end: newBox.fiscal_year_end || '31/12',
          share_capital: newBox.share_capital || 0,
          fiscal_residence: newBox.fiscal_residence || 'France',
          creation_date: newBox.creation_date,
          managers: newBox.managers,
          has_acre: newBox.has_acre,
          has_cga: newBox.has_cga,
          tva_regime: newBox.tva_regime,
          dividend_percent: newBox.dividend_percent,
          has_employees: newBox.has_employees,
          employees_count: newBox.employees_count,
          employees_avg_salary: newBox.employees_avg_salary,
          employees_months: newBox.employees_months,
          employee_profiles: newBox.employee_profiles,
          charges_mode: newBox.charges_mode,
          charges_data: newBox.charges_data,
          fiscal_params: newBox.fiscal_params,
          advanced_options: newBox.advanced_options,
          forecast_start_date: newBox.forecast_start_date || newBox.forecastStartDate,
          forecast_end_date: newBox.forecast_end_date || newBox.forecastEndDate,
          exercice_data: newBox.exercice_data,
          _tempId: newItem.uniqueBoxId
        };
        updates.entities = [...(current.entities || []), newEntity];
      }

      return updates;
    });
  };

  const removeBox = async (laneId, boxId) => {
    // Find the item to get its DB id for API deletion (use items or boxes)
    const itemsArray = scenario?.items || scenario?.boxes || [];
    const itemToRemove = itemsArray.find(b => b.laneId === laneId && b.uniqueBoxId === boxId);

    // Delete from database via API
    if (itemToRemove) {
      try {
        const dbId = itemToRemove.id || itemToRemove.individual_id || itemToRemove.entity_id;
        if (dbId && !String(dbId).startsWith('temp-')) {
          if (itemToRemove.type === 'individual' || itemToRemove.content === 'Individu') {
            await apiService.deleteIndividual(dbId);
          } else if (itemToRemove.type === 'entity' || itemToRemove.content === 'Entity') {
            await apiService.deleteEntity(dbId);
          }
        }
      } catch (error) {
        console.error('Error deleting from database:', error);
      }
    }

    // Update local state
    updateScenario(scenario.id, (current) => {
      // Find relations being removed to also remove their shareholdings
      const removedRelations = current.relations.filter(
        (relation) =>
          (relation.startBox.laneId === laneId && relation.startBox.boxId === boxId) ||
          (relation.endBox.laneId === laneId && relation.endBox.boxId === boxId)
      );
      const removedDbIds = removedRelations.map(r => r.dbId).filter(Boolean);

      // Filter items (single source of truth)
      const newItems = (current.items || current.boxes || []).filter(
        (item) => !(item.laneId === laneId && item.uniqueBoxId === boxId)
      );

      const updates = {
        ...current,
        items: newItems,        // Single source of truth
        boxes: newItems,        // Alias for backward compatibility
        relations: current.relations.filter(
          (relation) =>
            !(
              (relation.startBox.laneId === laneId && relation.startBox.boxId === boxId) ||
              (relation.endBox.laneId === laneId && relation.endBox.boxId === boxId)
            )
        ),
        // Also remove corresponding shareholdings
        shareholdings: (current.shareholdings || []).filter(
          (sh) => !removedDbIds.includes(sh.id) && !removedDbIds.includes(sh.dbId)
        ),
      };

      // Also remove from individuals or entities arrays for backend compatibility
      if (itemToRemove) {
        const isIndividual = itemToRemove.type === 'individual' || itemToRemove.content === 'Individu';
        if (isIndividual) {
          updates.individuals = (current.individuals || []).filter(ind =>
            ind._tempId !== boxId && ind.id !== itemToRemove.id && ind.id !== itemToRemove.individual_id
          );
        } else {
          updates.entities = (current.entities || []).filter(ent =>
            ent._tempId !== boxId && ent.id !== itemToRemove.id && ent.id !== itemToRemove.entity_id
          );
        }
      }

      return updates;
    });
  };

  const updateBox = (laneId, boxId, updatedData) => {
    updateScenario(scenario.id, (current) => {
      // Update items array (single source of truth)
      const newItems = (current.items || current.boxes || []).map((item) =>
        item.uniqueBoxId === boxId ? { ...item, ...updatedData } : item
      );

      const updates = {
        ...current,
        items: newItems,        // Single source of truth
        boxes: newItems,        // Alias for backward compatibility
      };

      // Also update individuals or entities arrays for backend compatibility
      const item = (current.items || current.boxes || []).find(b => b.uniqueBoxId === boxId);
      if (item) {
        const isIndividual = item.type === 'individual' || item.content === 'Individu';
        if (isIndividual) {
          updates.individuals = (current.individuals || []).map(ind => {
            if (ind._tempId === boxId || ind.id === item.id || ind.id === item.individual_id) {
              return { ...ind, ...updatedData };
            }
            return ind;
          });
        } else {
          updates.entities = (current.entities || []).map(ent => {
            if (ent._tempId === boxId || ent.id === item.id || ent.id === item.entity_id) {
              return { ...ent, ...updatedData };
            }
            return ent;
          });
        }
      }

      return updates;
    });
  };

  return (
    <div ref={scenarioRef} className="scenario" onMouseMove={handleMouseMove}>
      <ScenarioHeader
        scenario={scenario}
        updateScenario={updateScenario}
        addLane={addLane}
        dansMaPoche={dansMaPoche}
        isCalculating={isCalculating}
      />
      <div className="lanes-container" data-testid="lanes-container">
        {lanes.map((lane) => (
          <Lane
            key={lane.id}
            lane={lane}
            boxes={boxes.filter((box) => box.laneId === lane.id)}
            removeLane={removeLane}
            selectedBoxes={selectedBoxes}
            onBoxClick={onBoxClick}
            startLineDrawing={startLineDrawing}
            endLineDrawing={endLineDrawing}
            addBox={addBox}
            removeBox={removeBox}
            updateBox={updateBox}
            relations={relations}
            entities={scenario.entities || []}
            individuals={scenario.individuals || []}
            scenarioId={scenario.id}
            primaryIndividualId={scenario.primaryIndividualId || scenario.primary_individual_id}
            onSetPrimary={handleSetPrimaryIndividual}
          />
        ))}
      </div>
      <svg
        ref={svgRef}
        className="lines-container"
        data-testid="svg-container"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <g style={{ pointerEvents: "auto" }}>
          {relations.map((relation) => {
            console.log('[Scenario] Rendering relation:', relation);
            return (
              <Relation
                key={relation.id}
                relation={relation}
                onDelete={deleteRelation}
                onEdit={() => setEditingRelation(relation)}
                isHighlighted={highlightedRelation === relation.id}
              />
            );
          })}
        </g>
        {currentLine && (
          <line
            x1={currentLine.startX}
            y1={currentLine.startY}
            x2={currentLine.endX}
            y2={currentLine.endY}
            stroke="blue"
            strokeWidth="2"
          />
        )}
      </svg>
      {editingRelation && (() => {
        // Find the individual and entity for the modal
        const startBox = boxes.find(b => b.uniqueBoxId === editingRelation.startBox.boxId);
        const endBox = boxes.find(b => b.uniqueBoxId === editingRelation.endBox.boxId);

        // Determine if start is individual or entity
        const individual = startBox?.content === 'Individu' ? startBox : null;
        const entity = endBox?.content === 'Entity' ? endBox : null;

        return (
          <RelationModal
            relation={editingRelation}
            individual={individual}
            entity={entity}
            onConfirm={updateRelation}
            onClose={() => {
              setEditingRelation(null);
              if (pendingRelation) {
                setPendingRelation(null);
              }
            }}
            existingRelations={relations.filter(
              (rel) =>
                rel.endBox.laneId === editingRelation.endBox.laneId &&
                rel.endBox.boxId === editingRelation.endBox.boxId &&
                rel.id !== editingRelation.id
            )}
          />
        );
      })()}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default Scenario;